import { useEffect, useMemo, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { sessionSignal } from '../auth/session';
import { showToast } from '../components/Toaster';
import { showConfirm } from '../components/Confirmer';
import './TypicalsPage.css';

/**
 * Management surface for Schema-Templates (post J.18 rename of the old
 * TypicalDefinition surface). Read-only + delete: templates get created
 * from the Devices tab via "Als Template speichern" and consumed from the
 * Cred-Probe modal via "Template anwenden". This page is the catalog.
 *
 * Route lives on /settings/typicals for now (route rename tracked as
 * follow-up); the label + title + endpoints all switched over.
 */

type SchemaTemplateScope = 0 | 1; // 0 = Tenant, 1 = Global

interface SchemaTemplateSummary {
  id: string;
  name: string;
  protocol: string;
  scope: SchemaTemplateScope;
  version: number;
  updatedAt: string;
}

interface SchemaTemplateDetail {
  id: string;
  name: string;
  description: string | null;
  protocol: string;
  scope: SchemaTemplateScope;
  version: number;
  bodyJson: string;
  ipAddress: string | null;
  port: number | null;
  hasCredentials: boolean;
  createdAt: string;
  updatedAt: string;
}

export function TypicalsPage() {
  const permissions = sessionSignal.value.user?.permissions ?? [];
  const canEdit = permissions.includes('plants.schema.edit');

  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [protocolFilter, setProtocolFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const list = useAsync(
    (signal) => api.get<SchemaTemplateSummary[]>('/api/schema-templates', signal),
    [refreshTick],
  );

  const templates = list.status === 'ok' ? list.data : [];
  const protocols = useMemo(() => {
    const set = new Set<string>();
    for (const t of templates) set.add(t.protocol);
    return Array.from(set).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (protocolFilter && t.protocol !== protocolFilter) return false;
      if (q && !t.name.toLowerCase().includes(q) && !t.protocol.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [templates, protocolFilter, search]);

  // Auto-select first row on initial load so the detail pane is never empty.
  useEffect(() => {
    if (list.status !== 'ok') return;
    if (selectedId && filtered.some((t) => t.id === selectedId)) return;
    setSelectedId(filtered.length > 0 ? filtered[0].id : null);
  }, [list.status === 'ok' ? filtered : null]);

  const refresh = () => setRefreshTick((t) => t + 1);

  return (
    <AppShell title="Schema-Vorlagen">
      <div class="hv-templates">
        <aside class="hv-templates__side">
          <div class="hv-templates__side-head">
            <input
              type="search"
              class="hv-templates__search"
              placeholder="Suchen…"
              value={search}
              onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            />
            <div class="hv-templates__chips">
              <button
                type="button"
                class={`hv-templates__chip${protocolFilter === null ? ' hv-templates__chip--active' : ''}`}
                onClick={() => setProtocolFilter(null)}
              >
                Alle
                <span class="hv-templates__chip-count">{templates.length}</span>
              </button>
              {protocols.map((p) => {
                const count = templates.filter((t) => t.protocol === p).length;
                return (
                  <button
                    key={p}
                    type="button"
                    class={`hv-templates__chip${protocolFilter === p ? ' hv-templates__chip--active' : ''}`}
                    onClick={() => setProtocolFilter(p)}
                  >
                    {p}
                    <span class="hv-templates__chip-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <ul class="hv-templates__list">
            {list.status === 'loading' && <li class="hv-templates__muted">Lade…</li>}
            {list.status === 'error' && <li class="hv-templates__muted">Konnte die Vorlagen nicht laden.</li>}
            {list.status === 'ok' && filtered.length === 0 && (
              <li class="hv-templates__muted">
                {templates.length === 0
                  ? 'Noch keine Vorlagen. Über ein Device auf der Anlagen-Seite via „Als Template speichern" anlegen.'
                  : 'Keine Vorlagen mit diesem Filter.'}
              </li>
            )}
            {filtered.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  class={`hv-templates__item${t.id === selectedId ? ' hv-templates__item--active' : ''}`}
                  onClick={() => setSelectedId(t.id)}
                >
                  <div class="hv-templates__item-title">
                    <span class="hv-templates__item-name">{t.name}</span>
                    <span class={`hv-templates__scope hv-templates__scope--${t.scope === 1 ? 'global' : 'tenant'}`}>
                      {t.scope === 1 ? 'Global' : 'Tenant'}
                    </span>
                  </div>
                  <div class="hv-templates__item-meta">
                    <code class="mono">{t.protocol}</code>
                    <span>v{t.version}</span>
                    <span class="hv-templates__item-date">{formatDate(t.updatedAt)}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <section class="hv-templates__detail">
          {selectedId ? (
            <Detail
              key={selectedId}
              templateId={selectedId}
              canEdit={canEdit}
              onDeleted={() => {
                setSelectedId(null);
                refresh();
              }}
              onRenamed={() => refresh()}
            />
          ) : (
            <div class="hv-templates__empty">
              <p class="muted">
                Keine Vorlage ausgewählt. Vorlagen entstehen aus Geräten mit vollständigem Baum via
                „Als Template speichern" auf der Anlagen-Seite.
              </p>
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}

interface DetailProps {
  templateId: string;
  canEdit: boolean;
  onDeleted(): void;
}

interface DetailProps2 extends DetailProps { onRenamed?(newName: string): void }

function Detail({ templateId, canEdit, onDeleted, onRenamed }: DetailProps2) {
  const [deleting, setDeleting] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const state = useAsync(
    (signal) => api.get<SchemaTemplateDetail>(`/api/schema-templates/${templateId}`, signal),
    [templateId, refreshTick],
  );

  if (state.status === 'loading') return <p class="muted">Lade Vorlage…</p>;
  if (state.status === 'error') return <p class="muted">Konnte Vorlage nicht laden.</p>;
  const t = state.data;

  const summary = summariseBody(t.bodyJson);

  const startRename = () => {
    setRenameDraft(t.name);
    setRenaming(true);
  };
  const cancelRename = () => {
    setRenaming(false);
    setRenameDraft('');
  };
  const commitRename = async () => {
    const next = renameDraft.trim();
    if (next.length === 0 || next === t.name) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    try {
      await api.patch<void>(`/api/schema-templates/${t.id}`, { name: next });
      showToast({
        tone: 'success',
        title: 'Umbenannt',
        message: `Vorlage heißt jetzt "${next}".`,
        autoHideAfterMs: 3000,
      });
      setRenaming(false);
      setRenameDraft('');
      setRefreshTick((n) => n + 1);
      onRenamed?.(next);
    } catch (err) {
      showToast({
        tone: 'error',
        title: 'Umbenennen fehlgeschlagen',
        message: describeError(err),
        autoHideAfterMs: 6000,
      });
    } finally {
      setRenameSaving(false);
    }
  };

  const del = async () => {
    if (!canEdit) return;
    const ok = await showConfirm({
      title: 'Vorlage löschen?',
      message: `"${t.name}" wird endgültig gelöscht. Bereits angewendete Materialisierungen bleiben unberührt.`,
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.delete<void>(`/api/schema-templates/${t.id}`);
      showToast({
        tone: 'success',
        title: 'Vorlage gelöscht',
        message: `"${t.name}" wurde entfernt.`,
        autoHideAfterMs: 3000,
      });
      onDeleted();
    } catch (err) {
      setDeleting(false);
      showToast({
        tone: 'error',
        title: 'Löschen fehlgeschlagen',
        message: describeError(err),
        autoHideAfterMs: 6000,
      });
    }
  };

  return (
    <div class="hv-templates__detail-body">
      <header class="hv-templates__detail-head">
        <div>
          <h2 class="hv-templates__detail-title">
            {renaming ? (
              <input
                type="text"
                class="hv-templates__detail-title-input"
                value={renameDraft}
                autoFocus
                disabled={renameSaving}
                onInput={(e) => setRenameDraft((e.currentTarget as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); void commitRename(); }
                  else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                }}
                onBlur={() => { if (!renameSaving) void commitRename(); }}
                aria-label="Name der Vorlage"
              />
            ) : t.name}
            <span class="hv-templates__detail-version">v{t.version}</span>
          </h2>
          <div class="hv-templates__detail-sub">
            <code class="mono">{t.protocol}</code>
            <span class={`hv-templates__scope hv-templates__scope--${t.scope === 1 ? 'global' : 'tenant'}`}>
              {t.scope === 1 ? 'Global' : 'Tenant'}
            </span>
            {t.hasCredentials && (
              <span class="hv-templates__badge hv-templates__badge--creds">
                Credentials eingebettet
              </span>
            )}
          </div>
          {t.description && <p class="hv-templates__detail-desc">{t.description}</p>}
        </div>
        <div class="hv-templates__detail-actions">
          {!renaming && (
            <button
              type="button"
              class="hv-btn"
              disabled={!canEdit || deleting}
              onClick={startRename}
              title="Vorlage umbenennen"
            >
              Umbenennen
            </button>
          )}
          {renaming && (
            <button
              type="button"
              class="hv-btn"
              disabled={renameSaving}
              onClick={cancelRename}
            >
              Abbrechen
            </button>
          )}
          <button
            type="button"
            class="hv-btn hv-btn--danger"
            disabled={!canEdit || deleting || renaming}
            onClick={del}
          >
            {deleting ? 'Lösche…' : 'Löschen'}
          </button>
        </div>
      </header>

      <div class="hv-templates__grid">
        <div class="hv-templates__card">
          <div class="hv-templates__card-title">Umfang</div>
          <dl class="hv-templates__dl">
            <dt>Nodes</dt><dd>{summary.nodes.toLocaleString('de-DE')}</dd>
            <dt>Leaves</dt><dd>{summary.leaves.toLocaleString('de-DE')}</dd>
            {summary.deviceName && (<><dt>Device-Symbol</dt><dd><code class="mono">{summary.deviceSymbol ?? '–'}</code></dd></>)}
          </dl>
        </div>
        <div class="hv-templates__card">
          <div class="hv-templates__card-title">Verbindungs-Prefill</div>
          <dl class="hv-templates__dl">
            <dt>IP</dt><dd>{t.ipAddress ? <code class="mono">{t.ipAddress}</code> : <span class="muted">nicht gesetzt</span>}</dd>
            <dt>Port</dt><dd>{t.port ?? <span class="muted">nicht gesetzt</span>}</dd>
          </dl>
        </div>
        <div class="hv-templates__card">
          <div class="hv-templates__card-title">Metadaten</div>
          <dl class="hv-templates__dl">
            <dt>ID</dt><dd><code class="mono">{t.id}</code></dd>
            <dt>Erstellt</dt><dd>{formatDate(t.createdAt)}</dd>
            <dt>Geändert</dt><dd>{formatDate(t.updatedAt)}</dd>
          </dl>
        </div>
      </div>

      <details class="hv-templates__body-details">
        <summary>Snapshot-JSON anzeigen ({t.bodyJson.length.toLocaleString('de-DE')} Zeichen)</summary>
        <pre class="hv-templates__body mono">{prettifyIfPossible(t.bodyJson)}</pre>
      </details>
    </div>
  );
}

function summariseBody(bodyJson: string): { nodes: number; leaves: number; deviceSymbol: string | null; deviceName: string | null } {
  try {
    const parsed = JSON.parse(bodyJson) as {
      device?: { symbol?: string; name?: string };
      nodes?: unknown[];
      leaves?: unknown[];
    };
    return {
      nodes: Array.isArray(parsed.nodes) ? parsed.nodes.length : 0,
      leaves: Array.isArray(parsed.leaves) ? parsed.leaves.length : 0,
      deviceSymbol: parsed.device?.symbol ?? null,
      deviceName: parsed.device?.name ?? null,
    };
  } catch {
    return { nodes: 0, leaves: 0, deviceSymbol: null, deviceName: null };
  }
}

function prettifyIfPossible(source: string): string {
  try { return JSON.stringify(JSON.parse(source), null, 2); }
  catch { return source; }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear() % 100} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function describeError(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const b = err.body as { error?: string };
    return b.error ?? `HTTP ${err.status}`;
  }
  return err instanceof Error ? err.message : 'Unbekannter Fehler.';
}
